import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useLocation, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import PublicNav from "../../components/layout/PublicNav";
import PublicFooter from "../../components/layout/PublicFooter";
import SMSRecruiterCard, { SMS_COPY } from "../../components/SMSRecruiterCard";
import PageMeta from "../../components/seo/PageMeta";
import SMSConsentBlock from "../../components/SMSConsentBlock";
import { findChecklist, RATING_LABELS } from "../../data/checklistsData";
import {
  ClipboardCheck, ChevronDown, Save, Send, Printer, Download,
  ShieldCheck, CheckCircle2, ArrowRight, Sparkles, RefreshCcw, X,
  MessageSquare, Briefcase, FileText,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/*  Shared dynamic checklist detail page                              */
/*  Route: /compliance/checklists/:type/:slug                         */
/* ------------------------------------------------------------------ */

const TYPE_LABELS = {
  nurse: "Nurse",
  therapist: "Therapist",
  other: "Allied & Other",
};

const emptyAnswers = () => ({}); // { sectionIdx: { questionIdx: { rating, notes } } }

const emptyCandidate = () => ({
  fullName: "",
  email: "",
  phone: "",
  specialty: "",
  licenseState: "",
  yearsOfExperience: "",
  ssnLast4: "",
  recruiterName: "",
  assignmentInterest: "",
  smsOptIn: false,
});

function storageKey(type, slug) {
  return `tc_checklist_${type}_${slug}_v1`;
}

export default function ChecklistForm() {
  const { slug } = useParams();
  const { pathname } = useLocation();
  // Extract type from pathname: /compliance/checklists/<type>/<slug>
  const type = pathname.split("/")[3];
  const checklist = useMemo(() => findChecklist(type, slug), [type, slug]);

  // If unknown checklist, fall back to category landing
  if (!checklist) return <Navigate to={`/compliance/checklists/${type || ""}`} replace />;

  return <ChecklistFormInner checklist={checklist} />;
}

function ChecklistFormInner({ checklist }) {
  const KEY = storageKey(checklist.type, checklist.slug);

  const [candidate, setCandidate] = useState(() => loadField(KEY, "candidate", emptyCandidate()));
  const [answers, setAnswers] = useState(() => loadField(KEY, "answers", emptyAnswers()));
  const [attested, setAttested] = useState(() => loadField(KEY, "attested", false));
  const [openSection, setOpenSection] = useState(0);
  const [submitted, setSubmitted] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);   // null | { message }
  const [requestId, setRequestId] = useState(() => {
    try { return (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `req_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`; }
    catch { return `req_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`; }
  });
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [errors, setErrors] = useState({});
  const saveTimer = useRef(null);

  /* Autosave with debounce */
  useEffect(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      try {
        window.localStorage.setItem(
          KEY,
          JSON.stringify({ candidate, answers, attested, savedAt: new Date().toISOString() }),
        );
        setLastSavedAt(new Date());
      } catch {
        /* ignore */
      }
    }, 500);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
  }, [KEY, candidate, answers, attested]);

  /* ----- Stats ----- */
  const totalQuestions = useMemo(
    () => checklist.sections.reduce((n, s) => n + s.questions.length, 0),
    [checklist],
  );
  const ratedQuestions = useMemo(() => {
    let c = 0;
    Object.values(answers || {}).forEach((sec) => {
      Object.values(sec || {}).forEach((q) => {
        if (q && q.rating) c += 1;
      });
    });
    return c;
  }, [answers]);
  const completionPct = totalQuestions ? Math.round((ratedQuestions / totalQuestions) * 100) : 0;

  /* ----- Update helpers ----- */
  const updateCandidate = (patch) => setCandidate((prev) => ({ ...prev, ...patch }));

  const setRating = (sectionIdx, questionIdx, rating) =>
    setAnswers((prev) => {
      const next = { ...prev };
      next[sectionIdx] = { ...(prev[sectionIdx] || {}) };
      next[sectionIdx][questionIdx] = { ...(next[sectionIdx][questionIdx] || {}), rating };
      return next;
    });

  const setNotes = (sectionIdx, questionIdx, notes) =>
    setAnswers((prev) => {
      const next = { ...prev };
      next[sectionIdx] = { ...(prev[sectionIdx] || {}) };
      next[sectionIdx][questionIdx] = { ...(next[sectionIdx][questionIdx] || {}), notes };
      return next;
    });

  /* ----- Validation ----- */
  const validateForSubmission = () => {
    const e = {};
    if (!candidate.fullName.trim()) e.fullName = "Required";
    if (!candidate.email.trim()) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidate.email)) e.email = "Invalid email";
    if (!candidate.phone.trim()) e.phone = "Required";
    if (!candidate.specialty.trim()) e.specialty = "Required";
    if (!candidate.licenseState.trim()) e.licenseState = "Required";
    if (!candidate.yearsOfExperience.toString().trim()) e.yearsOfExperience = "Required";
    if (!attested) e.attested = "Attestation required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ----- Actions ----- */
  const handleSaveNow = () => {
    try {
      window.localStorage.setItem(
        KEY,
        JSON.stringify({ candidate, answers, attested, savedAt: new Date().toISOString() }),
      );
      setLastSavedAt(new Date());
      toast.success("Progress saved", {
        description: "Your checklist is stored locally on this device.",
      });
    } catch {
      toast.error("Couldn't save — local storage is unavailable.");
    }
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (!validateForSubmission()) {
      toast.error("Please complete all required fields and attest before submitting.");
      if (!ratedQuestions) setOpenSection(0);
      return;
    }
    const completion = totalQuestions ? Math.round((ratedQuestions / totalQuestions) * 100) : 0;
    const sourcePage = (typeof window !== "undefined" && window.location?.pathname) || `/compliance/checklists/${checklist.type}/${checklist.slug}`;
    const submittedAt = new Date().toISOString();

    // Build the RICH payload — real section titles, real question text, real
    // rating labels. Recruiter PDF + email need this; numeric indexes alone
    // are not human-readable.
    const ratingValuesForAvg = [];
    const sectionsRich = (checklist.sections || []).map((sec, sIdx) => ({
      sectionTitle: sec.title || `Section ${sIdx + 1}`,
      sectionDescription: sec.description || "",
      questions: (sec.questions || []).map((qText, qIdx) => {
        const cell = (answers?.[sIdx]?.[qIdx]) || {};
        const ratingValue = typeof cell.rating === "number" ? cell.rating : null;
        if (ratingValue) ratingValuesForAvg.push(ratingValue);
        return {
          itemNumber: qIdx + 1,
          questionText: typeof qText === "string" ? qText : (qText?.text || String(qText)),
          ratingValue,
          ratingLabel: ratingValue ? (RATING_LABELS[ratingValue]?.label || RATING_LABELS[ratingValue]?.short || "") : "",
          notes: cell.notes || "",
        };
      }),
    }));
    const averageRating = ratingValuesForAvg.length
      ? Math.round((ratingValuesForAvg.reduce((a, b) => a + b, 0) / ratingValuesForAvg.length) * 10) / 10
      : 0;
    const attestationText = "I certify that the information provided in this skills checklist is true and accurate to the best of my knowledge. I understand any misrepresentation may disqualify me from consideration and that all credentials will be verified by TRITAL Care® before submission to any client.";

    const richPayload = {
      request_id: requestId,
      submittedAt,
      source_page: sourcePage,
      checklist: {
        type: checklist.type,
        slug: checklist.slug,
        title: checklist.title,
        category: checklist.category || "",
        totalSections: (checklist.sections || []).length,
        totalQuestions,
      },
      candidate: {
        fullName: candidate.fullName,
        email: candidate.email,
        phone: candidate.phone,
        specialty: checklist.title,
        licenseState: candidate.licenseState,
        yearsOfExperience: candidate.years || candidate.yearsOfExperience || "",
        recruiterName: candidate.recruiter || candidate.recruiterName || "",
        assignmentInterest: candidate.assignmentInterest || "",
        smsOptIn: !!candidate.smsOptIn,
      },
      attestation: {
        accepted: !!attested,
        text: attestationText,
        timestamp: attested ? submittedAt : "",
      },
      stats: {
        ratedQuestions,
        totalQuestions,
        completion_pct: completion,
        averageRating,
      },
      sections: sectionsRich,
    };

    // Keep a local cache (debug only). Public download is PDF — never JSON.
    try { window.localStorage.setItem(KEY + ":submission", JSON.stringify(richPayload)); } catch { /* ignore */ }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const r = await fetch(`/api/checklist-submissions`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Idempotency-Key": requestId },
        body: JSON.stringify(richPayload),
      });

      let data = null;
      try { data = await r.json(); } catch { /* non-JSON */ }

      if (!r.ok || !data || data.success === false || data.ok === false) {
        // Internal-notification failure path: show the error modal ("Submission Not Sent")
        setSubmitError({
          message: (data && data.message) || `Submission failed (HTTP ${r.status}). Please try again.`,
          internal_email_error: data && data.internal_email_error,
          pdf_status: data && data.pdf_status,
        });
        toast.error("We couldn't send your checklist. Please try again or text our recruiter.");
        return;
      }

      // Server accepted — open success modal and stash everything (incl. pdf_base64) for download.
      setSubmitted({ ...richPayload, server: data });
      if (data.idempotent_replay) {
        toast.success("Checklist already received — duplicate ignored.");
      } else if (data.internal_notification_status === "sent" && data.candidate_confirmation_status === "sent") {
        toast.success("Checklist received by TRITAL Care.", { description: "Confirmation emails sent. PDF copy attached." });
      } else if (data.internal_notification_status === "sent") {
        toast.warning("Recruiter notified — candidate confirmation could not be sent.", { description: "Please download your PDF copy below." });
      } else if (data.candidate_confirmation_status === "sent") {
        toast.warning("Confirmation sent — internal recruiter notification failed.", { description: "Please download your PDF copy below." });
      } else {
        toast.warning("Checklist received locally — email notifications failed.", { description: "Please download your PDF copy below." });
      }
      try { setRequestId((window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `req_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`); } catch { /* ignore */ }
    } catch (err) {
      setSubmitError({ message: "We could not reach our server. Please check your connection and try again." });
      toast.error("We couldn't send your checklist at this moment. Please try again or text our recruiter.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    if (!window.confirm("Clear progress for this checklist on this device?")) return;
    window.localStorage.removeItem(KEY);
    window.localStorage.removeItem(KEY + ":submission");
    setCandidate(emptyCandidate());
    setAnswers(emptyAnswers());
    setAttested(false);
    setSubmitted(null);
    setErrors({});
    toast("Checklist cleared.", { description: "You can start fresh anytime." });
  };

  const handlePrint = () => window.print();

  const handleDownloadPdf = () => {
    const server = submitted?.server;
    if (!server || !server.pdf_base64) {
      toast.error("PDF not yet generated.", { description: "Submit the checklist first to receive your branded PDF copy." });
      return;
    }
    try {
      // Decode base64 → Blob → trigger browser download
      const byteChars = atob(server.pdf_base64);
      const byteNums = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);
      const byteArr = new Uint8Array(byteNums);
      const blob = new Blob([byteArr], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = server.pdf_filename || `TRITAL_Care_${(candidate.fullName || "Candidate").replace(/[^A-Za-z0-9]+/g, "_")}_Skills_Checklist.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("PDF downloaded.");
    } catch {
      toast.error("Couldn't decode PDF. Please retry or contact careers@tritalcare.com.");
    }
  };

  const todayString = new Date().toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="min-h-screen bg-white" data-testid={`checklist-form-${checklist.type}-${checklist.slug}`}>
      <PageMeta
        title={`${checklist.title} Skills Checklist | TRITAL Care®`}
        description={`Complete the TRITAL Care® ${checklist.title} skills checklist. Self-rate competency on a 1–4 scale, save progress, and submit for recruiter review.`}
        canonical={`https://www.tritalcare.com/compliance/checklists/${checklist.type}/${checklist.slug}`}
      />
      <PublicNav />

      {/* HERO ------------------------------------------------------ */}
      <section className="relative overflow-hidden bg-trital-mesh">
        <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-5 lg:px-8 pt-12 pb-10 lg:pt-16 lg:pb-12">
          <nav aria-label="Breadcrumb" className="text-xs text-slate-500 mb-5">
            <Link to="/" className="hover:text-trital-navy">Home</Link>
            <span className="mx-2 text-slate-300">/</span>
            <Link to="/compliance" className="hover:text-trital-navy">Compliance</Link>
            <span className="mx-2 text-slate-300">/</span>
            <Link to="/compliance/checklists" className="hover:text-trital-navy">Checklists</Link>
            <span className="mx-2 text-slate-300">/</span>
            <Link to={`/compliance/checklists/${checklist.type}`} className="hover:text-trital-navy">
              {TYPE_LABELS[checklist.type]}
            </Link>
            <span className="mx-2 text-slate-300">/</span>
            <span className="text-slate-700">{checklist.title}</span>
          </nav>

          <div className="grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-card">
                <ClipboardCheck className="w-3.5 h-3.5 text-trital-navy" strokeWidth={1.8} />
                <span className="overline text-trital-navy">{checklist.category} · {TYPE_LABELS[checklist.type]}</span>
              </div>
              <h1 className="mt-5 font-display font-black text-3xl lg:text-[44px] leading-[1.06] tracking-tight text-slate-900 text-balance">
                {checklist.title}
                <span className="block text-trital-navy text-[20px] lg:text-[24px] font-bold mt-2">Skills Checklist</span>
              </h1>
              <p className="mt-4 text-slate-600 max-w-xl leading-relaxed">{checklist.summary}</p>
              <div className="mt-5 flex flex-wrap items-center gap-2 text-[12px] text-slate-500">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-slate-200">
                  <ClipboardCheck className="w-3 h-3 text-trital-red" strokeWidth={1.8} />
                  {checklist.sections.length} sections · {totalQuestions} items
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-slate-200">
                  <Save className="w-3 h-3 text-trital-navy" strokeWidth={1.8} />
                  Autosaves on this device
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-slate-200">
                  <ShieldCheck className="w-3 h-3 text-trital-navy" strokeWidth={1.8} />
                  Private by default
                </span>
              </div>
            </div>

            {/* Progress card */}
            <div className="lg:col-span-5">
              <div
                className="rounded-2xl border border-slate-200 bg-white shadow-navy p-5 lg:p-6"
                data-testid="checklist-progress-card"
              >
                <div className="flex items-baseline justify-between">
                  <div className="overline text-trital-red">Your progress</div>
                  {lastSavedAt && (
                    <span className="text-[11px] text-slate-500">
                      Saved · {lastSavedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  )}
                </div>
                <div className="mt-3 flex items-end gap-3">
                  <div className="font-display font-black text-4xl text-trital-navy leading-none">
                    {completionPct}%
                  </div>
                  <div className="text-[12.5px] text-slate-600 pb-1">
                    {ratedQuestions} of {totalQuestions} items rated
                  </div>
                </div>
                <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-trital-navy to-trital-red transition-all duration-500"
                    style={{ width: `${completionPct}%` }}
                  />
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    onClick={handleSaveNow}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-300 text-[13px] font-semibold text-trital-navy hover:bg-slate-50"
                    data-testid="checklist-btn-save"
                  >
                    <Save className="w-3.5 h-3.5" strokeWidth={1.8} /> Save progress
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-trital-red text-white text-[13px] font-semibold btn-lift hover:bg-trital-red-deep disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="checklist-btn-submit"
                  >
                    <Send className="w-3.5 h-3.5" strokeWidth={1.8} /> {submitting ? "Submitting…" : "Submit to TRITAL Care"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CANDIDATE INFO ------------------------------------------- */}
      <section
        className="max-w-6xl mx-auto px-5 lg:px-8 py-10"
        data-testid="checklist-candidate-section"
      >
        <div className="rounded-3xl border border-slate-200 bg-white shadow-card p-6 lg:p-7">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <div>
              <div className="overline text-trital-red">Step 1</div>
              <h2 className="mt-1 font-display font-black text-[24px] lg:text-[28px] tracking-tight text-slate-900">
                Candidate information
              </h2>
              <p className="mt-1 text-[13.5px] text-slate-500">
                We use this to match your checklist to your TRITAL Care® profile and recruiter.
              </p>
            </div>
            <div className="text-[12px] text-slate-500">{todayString}</div>
          </div>

          <div className="mt-6 grid md:grid-cols-2 gap-5">
            <Field
              label="Full name *"
              value={candidate.fullName}
              onChange={(v) => updateCandidate({ fullName: v })}
              error={errors.fullName}
              testId="cand-fullName"
              placeholder="First and last"
            />
            <Field
              label="Email *"
              type="email"
              value={candidate.email}
              onChange={(v) => updateCandidate({ email: v })}
              error={errors.email}
              testId="cand-email"
              placeholder="you@example.com"
            />
            <Field
              label="Phone *"
              type="tel"
              value={candidate.phone}
              onChange={(v) => updateCandidate({ phone: v })}
              error={errors.phone}
              testId="cand-phone"
              placeholder="(555) 555-5555"
            />
            <Field
              label="Specialty *"
              value={candidate.specialty}
              onChange={(v) => updateCandidate({ specialty: v })}
              error={errors.specialty}
              testId="cand-specialty"
              placeholder={`e.g. ${checklist.title}`}
            />
            <Field
              label="License state *"
              value={candidate.licenseState}
              onChange={(v) => updateCandidate({ licenseState: v.toUpperCase().slice(0, 2) })}
              error={errors.licenseState}
              testId="cand-licenseState"
              placeholder="TX"
              maxLength={2}
            />
            <Field
              label="Years of experience *"
              type="number"
              value={candidate.yearsOfExperience}
              onChange={(v) => updateCandidate({ yearsOfExperience: v })}
              error={errors.yearsOfExperience}
              testId="cand-years"
              placeholder="e.g. 4"
              min="0"
              max="60"
              step="0.5"
            />
            <Field
              label="Last 4 of SSN (optional)"
              value={candidate.ssnLast4}
              onChange={(v) => updateCandidate({ ssnLast4: v.replace(/\D/g, "").slice(0, 4) })}
              testId="cand-ssn"
              placeholder="0000"
              inputMode="numeric"
              maxLength={4}
            />
            <Field
              label="Recruiter name (optional)"
              value={candidate.recruiterName}
              onChange={(v) => updateCandidate({ recruiterName: v })}
              testId="cand-recruiter"
              placeholder="If you've been working with one"
            />
            <Field
              label="Assignment you're interested in (optional)"
              value={candidate.assignmentInterest}
              onChange={(v) => updateCandidate({ assignmentInterest: v })}
              testId="cand-assignment"
              placeholder="e.g. ICU travel · 13-week · Texas"
              wrap
            />
          </div>

          {/* TCPA SMS Consent — ALWAYS visible (SimpleTexting requirement) */}
          <div className="mt-6" data-testid="cand-sms-consent-wrapper">
            <SMSConsentBlock
              checked={candidate.smsOptIn}
              onCheckedChange={(v) => updateCandidate({ smsOptIn: v })}
              checkboxId="checklist-sms-consent"
            />
          </div>
        </div>
      </section>

      {/* RATING-SCALE LEGEND -------------------------------------- */}
      <section className="max-w-6xl mx-auto px-5 lg:px-8" aria-label="Rating scale">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 lg:p-5">
          <div className="overline text-trital-red mb-3">Rating scale</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map((v) => (
              <div key={v} className="flex items-start gap-2.5">
                <div className={`w-7 h-7 rounded-lg ${RATING_LABELS[v].dot} flex items-center justify-center shrink-0`}>
                  <span className="text-white text-[12px] font-bold">{v}</span>
                </div>
                <div className="text-[12.5px] text-slate-700 leading-snug">
                  <div className="font-semibold text-slate-900">{v} · {RATING_LABELS[v].short}</div>
                  <div className="text-slate-500">{RATING_LABELS[v].label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTIONS ------------------------------------------------- */}
      <section
        className="max-w-6xl mx-auto px-5 lg:px-8 py-10 space-y-3"
        data-testid="checklist-sections"
      >
        <div className="overline text-trital-red mb-1">Step 2</div>
        <h2 className="font-display font-black text-[24px] lg:text-[28px] tracking-tight text-slate-900 mb-4">
          Competency ratings
        </h2>

        {checklist.sections.map((section, sIdx) => {
          const sectionAnswers = answers[sIdx] || {};
          const ratedInSection = Object.values(sectionAnswers).filter((q) => q?.rating).length;
          const isOpen = openSection === sIdx;
          return (
            <SectionAccordion
              key={sIdx}
              section={section}
              sectionIdx={sIdx}
              total={section.questions.length}
              ratedInSection={ratedInSection}
              isOpen={isOpen}
              onToggle={() => setOpenSection(isOpen ? -1 : sIdx)}
              answers={sectionAnswers}
              setRating={setRating}
              setNotes={setNotes}
            />
          );
        })}
      </section>

      {/* ATTESTATION ---------------------------------------------- */}
      <section className="max-w-6xl mx-auto px-5 lg:px-8 py-6" data-testid="checklist-attestation-section">
        <div className="overline text-trital-red mb-1">Step 3</div>
        <h2 className="font-display font-black text-[24px] lg:text-[28px] tracking-tight text-slate-900 mb-4">
          Attestation
        </h2>
        <label
          className={`flex items-start gap-3 p-5 rounded-2xl border cursor-pointer transition-colors ${
            attested
              ? "border-emerald-400 bg-emerald-50"
              : errors.attested
              ? "border-red-300 bg-red-50"
              : "border-slate-200 bg-slate-50 hover:border-trital-navy/40"
          }`}
          data-testid="checklist-attest-label"
        >
          <input
            type="checkbox"
            checked={!!attested}
            onChange={(e) => setAttested(e.target.checked)}
            className="mt-1 w-4 h-4 accent-trital-navy"
            data-testid="checklist-attest-checkbox"
          />
          <span className="text-[13.5px] text-slate-700 leading-relaxed">
            I certify that the information provided in this skills checklist is true and accurate. I understand that any
            misrepresentation or omission may affect my eligibility for assignment or employment.
          </span>
        </label>
        {errors.attested && (
          <div className="mt-2 text-[12.5px] text-red-600" data-testid="attest-error">
            {errors.attested}
          </div>
        )}
      </section>

      {/* COMPLIANCE HELP / SMS ------------------------------------ */}
      <section
        className="max-w-6xl mx-auto px-5 lg:px-8 py-6"
        data-testid="checklist-compliance-help"
      >
        <div className="rounded-2xl border border-trital-navy/15 bg-trital-medical-blue-soft/60 p-5 lg:p-6">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="w-10 h-10 rounded-xl bg-white text-trital-navy flex items-center justify-center shrink-0 border border-slate-200">
              <MessageSquare className="w-5 h-5" strokeWidth={1.8} />
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="font-display font-bold text-[17px] text-slate-900">
                Need help completing this checklist?
              </div>
              <p className="mt-1 text-[13.5px] text-slate-700">
                Text our team directly — share a screenshot, ask a question, get a real human reply within one business hour.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <a
                  href="sms:+18323441525"
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white border border-slate-200 text-[12.5px] font-semibold text-trital-navy hover:bg-slate-50"
                  data-testid="help-sms-recruiter"
                >
                  <Briefcase className="w-3.5 h-3.5" strokeWidth={1.8} />
                  Recruiter Jobs · (832) 344-1525
                </a>
                <a
                  href="sms:+12182502526"
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white border border-slate-200 text-[12.5px] font-semibold text-trital-red hover:bg-red-50"
                  data-testid="help-sms-compliance"
                >
                  <FileText className="w-3.5 h-3.5" strokeWidth={1.8} />
                  Compliance Docs · (218) 250-2526
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TOOLBAR --------------------------------------------------- */}
      <section className="max-w-6xl mx-auto px-5 lg:px-8 py-6">
        <div className="flex flex-wrap gap-2 justify-end">
          <button
            onClick={handlePrint}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-slate-300 text-[13px] font-semibold text-trital-navy hover:bg-slate-50"
            data-testid="checklist-btn-print"
          >
            <Printer className="w-3.5 h-3.5" strokeWidth={1.8} /> Print
          </button>
          <button
            onClick={handleDownloadPdf}
            disabled={!submitted}
            title={submitted ? "Download your branded TRITAL Care® checklist PDF" : "Available after submission"}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-slate-300 text-[13px] font-semibold text-trital-navy hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="checklist-btn-download"
          >
            <Download className="w-3.5 h-3.5" strokeWidth={1.8} /> {submitted ? "Download PDF" : "Download PDF (after submit)"}
          </button>
          <button
            onClick={handleReset}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-slate-300 text-[13px] font-semibold text-slate-500 hover:text-trital-red hover:border-trital-red"
            data-testid="checklist-btn-reset"
          >
            <RefreshCcw className="w-3.5 h-3.5" strokeWidth={1.8} /> Reset
          </button>
        </div>
      </section>

      {/* STICKY FOOTER -------------------------------------------- */}
      <div className="sticky bottom-0 z-30 border-t border-slate-200 bg-white/95 backdrop-blur-md print:hidden">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-3 flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-trital-medical-blue-soft flex items-center justify-center">
              <ClipboardCheck className="w-4 h-4 text-trital-navy" strokeWidth={1.8} />
            </div>
            <div className="min-w-0">
              <div className="text-[12px] text-slate-500 truncate">
                {ratedQuestions} / {totalQuestions} rated · {attested ? "Attested" : "Attestation required"}
              </div>
              <div className="h-1 mt-1 rounded-full bg-slate-100 overflow-hidden w-[180px] max-w-full">
                <div
                  className="h-full bg-gradient-to-r from-trital-navy to-trital-red transition-all"
                  style={{ width: `${completionPct}%` }}
                />
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSaveNow}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-slate-300 text-[13px] font-semibold text-trital-navy hover:bg-slate-50"
              data-testid="checklist-btn-save-sticky"
            >
              <Save className="w-3.5 h-3.5" strokeWidth={1.8} /> Save
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-trital-red text-white text-[13px] font-semibold btn-lift hover:bg-trital-red-deep disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="checklist-btn-submit-sticky"
            >
              <Send className="w-3.5 h-3.5" strokeWidth={1.8} /> {submitting ? "Submitting…" : "Submit"}
            </button>
          </div>
        </div>
      </div>

      {/* SUCCESS MODAL -------------------------------------------- */}
      <AnimatePresence>
        {submitted && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm print:hidden"
            data-testid="checklist-success-modal"
          >
            <motion.div
              initial={{ scale: 0.96, y: 10, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.96, y: 10, opacity: 0 }}
              transition={{ duration: 0.22 }}
              className="relative w-full max-w-lg rounded-3xl bg-white shadow-navy p-7 lg:p-8 border border-slate-200 overflow-hidden"
            >
              <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-trital-medical-blue-soft blur-3xl" />
              <div className="absolute -bottom-24 -left-20 w-64 h-64 rounded-full bg-red-100 blur-3xl" />
              <button
                onClick={() => setSubmitted(null)}
                className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 text-slate-500"
                aria-label="Close"
                data-testid="checklist-success-close"
              >
                <X className="w-4 h-4" strokeWidth={1.8} />
              </button>

              <div className="relative">
                <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                  <CheckCircle2 className="w-7 h-7" strokeWidth={1.8} />
                </div>
                <div className="overline text-trital-red mt-5 flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" strokeWidth={1.8} /> Checklist Received
                </div>
                {(() => {
                  const candidateOk = submitted?.server?.candidate_confirmation_status === "sent";
                  return (
                    <>
                      <h3 className="mt-2 font-display font-black text-[26px] leading-[1.15] text-slate-900 text-balance">
                        {candidateOk
                          ? "Thank you. Your skills checklist has been received by TRITAL Care."
                          : "Your checklist was received by TRITAL Care, but we could not send the candidate confirmation email."}
                      </h3>
                      <p className="mt-3 text-[14px] text-slate-600 leading-relaxed">
                        {candidateOk
                          ? "A confirmation email with a PDF copy has been sent to your email. Our recruiting team has also received your completed checklist."
                          : "Please download your PDF copy now or contact careers@tritalcare.com."}
                      </p>
                    </>
                  );
                })()}

                <dl className="mt-5 grid grid-cols-2 gap-3 text-[12.5px]">
                  <div className="rounded-xl border border-slate-200 p-3">
                    <dt className="text-slate-500">Items rated</dt>
                    <dd className="mt-1 font-display font-bold text-slate-900">
                      {submitted.stats.ratedQuestions} / {submitted.stats.totalQuestions}
                    </dd>
                  </div>
                  <div className="rounded-xl border border-slate-200 p-3">
                    <dt className="text-slate-500">Status</dt>
                    <dd className="mt-1 font-display font-bold text-slate-900" data-testid="checklist-success-status">
                      {submitted.server
                        && submitted.server.internal_notification_status === "sent"
                        && submitted.server.candidate_confirmation_status === "sent"
                        ? "Email notification sent"
                        : submitted.server && submitted.server.internal_notification_status === "sent"
                          ? "Recruiter notified · candidate email pending"
                          : "Checklist received"}
                    </dd>
                  </div>
                </dl>

                {submitted?.server?.candidate_confirmation_status === "sent" ? (
                  <p className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 text-[12.5px] text-emerald-900" data-testid="success-pdf-note">
                    Confirmation sent to <b>{submitted?.candidate?.email}</b>. Internal team also notified at <b>careers@tritalcare.com</b>.
                  </p>
                ) : (
                  <p className="mt-4 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-[12.5px] text-amber-900" data-testid="success-pdf-note">
                    Recruiting team has been notified at <b>careers@tritalcare.com</b>. We were unable to send the candidate confirmation email to <b>{submitted?.candidate?.email}</b> — please download your PDF copy now.
                  </p>
                )}

                <div className="mt-6 flex flex-wrap gap-2">
                  <button
                    onClick={handleDownloadPdf}
                    disabled={!submitted?.server?.pdf_base64}
                    className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-trital-red text-white text-[12.5px] font-semibold btn-lift hover:bg-trital-red-deep disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="success-download-pdf"
                  >
                    <Download className="w-3.5 h-3.5" strokeWidth={1.8} /> Download PDF
                  </button>
                  {submitted?.server?.candidate_confirmation_status !== "sent" && (
                    <a
                      href="mailto:careers@tritalcare.com?subject=Skills%20Checklist%20Submission%20-%20Candidate%20Confirmation%20Issue"
                      className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-slate-200 text-[12.5px] font-semibold text-trital-navy hover:bg-slate-50"
                      data-testid="success-email-careers"
                    >
                      <MessageSquare className="w-3.5 h-3.5" strokeWidth={1.8} /> Email careers@tritalcare.com
                    </a>
                  )}
                  <a
                    href="sms:+18323441525"
                    className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-slate-200 text-[12.5px] font-semibold text-trital-navy hover:bg-slate-50"
                    data-testid="success-text-recruiter"
                  >
                    <Briefcase className="w-3.5 h-3.5" strokeWidth={1.8} /> Text Recruiter
                  </a>
                  <Link
                    to="/clinicians"
                    className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-trital-navy text-white text-[12.5px] font-semibold btn-lift hover:bg-trital-navy-deep"
                    data-testid="success-search-opportunities"
                  >
                    Search Opportunities <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.8} />
                  </Link>
                  <Link
                    to="/compliance/checklists"
                    className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-slate-200 text-[12.5px] font-semibold text-slate-700 hover:bg-slate-50"
                    data-testid="success-back-checklists"
                  >
                    Return to Checklists
                  </Link>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ERROR MODAL — shown when submission fails (server unreachable / Resend error) */}
      <AnimatePresence>
        {submitError && !submitted && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/55 backdrop-blur-sm p-4"
            data-testid="checklist-error-modal"
            onClick={() => setSubmitError(null)}
          >
            <motion.div
              initial={{ scale: 0.96, y: 8 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 8 }}
              className="relative w-full max-w-md rounded-3xl bg-white shadow-navy p-7 border border-slate-200"
              onClick={(e) => e.stopPropagation()}
            >
              <button onClick={() => setSubmitError(null)} className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 text-slate-500" aria-label="Close">
                <X className="w-4 h-4" strokeWidth={1.8} />
              </button>
              <div className="w-14 h-14 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center">
                <RefreshCcw className="w-7 h-7" strokeWidth={1.8} />
              </div>
              <div className="overline text-trital-red mt-5">Submission Not Sent</div>
              <h3 className="mt-2 font-display font-black text-[22px] leading-tight text-slate-900">
                We could not send your checklist at this moment. Please try again or contact careers@tritalcare.com.
              </h3>
              <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">
                Your responses have been saved locally only — <b>no email notification was sent</b>. The TRITAL Care recruiting team has <b>not</b> received your checklist yet.
              </p>
              <div className="mt-2 text-[11.5px] text-slate-500 break-words">{submitError.message}</div>

              <div className="mt-5 grid gap-2">
                <button
                  onClick={() => { setSubmitError(null); handleSubmit(); }}
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-trital-red text-white text-[13px] font-semibold btn-lift hover:bg-trital-red-deep disabled:opacity-60"
                  data-testid="checklist-error-retry"
                >
                  <RefreshCcw className="w-3.5 h-3.5" strokeWidth={1.8} /> {submitting ? "Retrying…" : "Try Again"}
                </button>
                <a
                  href="sms:+18323441525"
                  className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-slate-300 text-[13px] font-semibold text-trital-navy hover:bg-slate-50"
                  data-testid="checklist-error-text-recruiter"
                >
                  <Briefcase className="w-3.5 h-3.5" strokeWidth={1.8} /> Text Recruiter
                </a>
                <a
                  href="mailto:careers@tritalcare.com?subject=Skills%20checklist%20submission%20issue"
                  className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-slate-300 text-[13px] font-semibold text-slate-700 hover:bg-slate-50"
                  data-testid="checklist-error-email"
                >
                  <MessageSquare className="w-3.5 h-3.5" strokeWidth={1.8} /> Email careers@tritalcare.com
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <section className="max-w-6xl mx-auto px-5 lg:px-8 pb-16" data-testid="checklist-detail-sms">
        <SMSRecruiterCard variant="default" headline={SMS_COPY.checklist.headline} subtext={SMS_COPY.checklist.sub} />
      </section>

      <PublicFooter />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Helper components                                                 */
/* ------------------------------------------------------------------ */

function loadField(key, field, fallback) {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const obj = JSON.parse(raw);
    return obj[field] !== undefined ? obj[field] : fallback;
  } catch {
    return fallback;
  }
}

function Field({
  label, value, onChange, error, type = "text", testId, placeholder,
  inputMode, maxLength, min, max, step, wrap = false,
}) {
  return (
    <label className={`block ${wrap ? "md:col-span-2" : ""}`}>
      <span className="text-[11.5px] font-bold tracking-[0.14em] uppercase text-trital-navy">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        maxLength={maxLength}
        min={min}
        max={max}
        step={step}
        className={`mt-2 w-full px-4 py-2.5 rounded-xl border bg-white text-[14px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-trital-navy/20 ${
          error ? "border-red-400" : "border-slate-300 focus:border-trital-navy"
        }`}
        data-testid={testId}
      />
      {error && (
        <span className="mt-1 block text-[11.5px] text-red-600" data-testid={`${testId}-error`}>
          {error}
        </span>
      )}
    </label>
  );
}

function SectionAccordion({
  section, sectionIdx, total, ratedInSection, isOpen, onToggle, answers, setRating, setNotes,
}) {
  return (
    <li
      className={`rounded-2xl bg-white border overflow-hidden transition-shadow ${
        isOpen ? "border-trital-navy shadow-navy" : "border-slate-200 shadow-card"
      }`}
      data-testid={`checklist-section-${sectionIdx}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left px-5 lg:px-6 py-5 flex items-center justify-between gap-4"
        aria-expanded={isOpen}
        data-testid={`checklist-section-${sectionIdx}-toggle`}
      >
        <div className="flex items-center gap-4 min-w-0">
          <div
            className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center font-display font-black text-[16px] ${
              ratedInSection > 0
                ? "bg-trital-navy text-white"
                : "bg-trital-medical-blue-soft text-trital-navy"
            }`}
          >
            {sectionIdx + 1}
          </div>
          <div className="min-w-0">
            <div className="font-display font-bold text-[16px] lg:text-[17px] text-slate-900 truncate">
              {section.title}
            </div>
            <div className="mt-0.5 text-[12px] text-slate-500">
              {ratedInSection} of {total} items rated
            </div>
          </div>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-trital-navy transition-transform ${isOpen ? "rotate-180" : ""}`}
          strokeWidth={1.8}
        />
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.24 }}
            className="overflow-hidden"
          >
            <ul className="px-5 lg:px-6 pb-5 border-t border-slate-100 space-y-3">
              {section.questions.map((q, qIdx) => {
                const ans = answers[qIdx] || {};
                return (
                  <li
                    key={qIdx}
                    className="rounded-xl border border-slate-200 p-4 bg-white"
                    data-testid={`checklist-section-${sectionIdx}-q-${qIdx}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="shrink-0 mt-0.5 w-6 h-6 rounded-full border border-slate-300 bg-white text-[11px] font-bold text-slate-500 flex items-center justify-center">
                        {qIdx + 1}
                      </span>
                      <span className="text-[14px] text-slate-800 leading-relaxed flex-1">{q}</span>
                    </div>

                    <div className="mt-3 flex items-center gap-2 flex-wrap" role="radiogroup" aria-label={q}>
                      {[1, 2, 3, 4].map((v) => {
                        const active = ans.rating === v;
                        return (
                          <button
                            key={v}
                            type="button"
                            onClick={() => setRating(sectionIdx, qIdx, v)}
                            title={RATING_LABELS[v].label}
                            aria-pressed={active}
                            aria-label={`Rate ${v} — ${RATING_LABELS[v].label}`}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold transition-all border ${
                              active
                                ? "bg-trital-navy text-white border-trital-navy"
                                : "bg-white border-slate-200 text-slate-600 hover:border-trital-navy/40"
                            }`}
                            data-testid={`rate-${sectionIdx}-${qIdx}-${v}`}
                          >
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${active ? "bg-white" : RATING_LABELS[v].dot}`}
                            />
                            <span className="font-display font-black text-[12px]">{v}</span>
                            <span className="hidden sm:inline">{RATING_LABELS[v].short}</span>
                          </button>
                        );
                      })}
                    </div>

                    <input
                      type="text"
                      placeholder="Notes (optional) — equipment, frequency, recency"
                      value={ans.notes || ""}
                      onChange={(e) => setNotes(sectionIdx, qIdx, e.target.value)}
                      className="mt-3 w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-[13px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-trital-navy/20 focus:border-trital-navy"
                      data-testid={`notes-${sectionIdx}-${qIdx}`}
                    />
                  </li>
                );
              })}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
}
